// src/lib/version-app.js — L'APPLICATION SAIT QU'ELLE EST PÉRIMÉE.
//
// L'INCIDENT (2026-09-22 au soir) : sur la fiche d'un blocage, « Débloquer »
// répondait « Il faut confirmer avant de remettre ces nuits en vente. Rien n'a
// été modifié. » Le serveur exigeait `confirm: true` depuis #1470 ; la fiche
// l'envoie depuis ce même commit. L'onglet de Dimitri tournait avec le
// JavaScript d'AVANT : un onglet ouvert depuis des jours ne recharge jamais le
// bundle CRA (hashé), et rien ne lui disait qu'une nouvelle version était en
// ligne. Le message serveur, vrai, était incompréhensible.
//
// LE MÉCANISME : deux empreintes du même commit.
//   - COMPILÉE : `process.env.REACT_APP_BUILD_SHA`, inlinée par CRA au build
//     (posée par le script `build` de package.json depuis
//     `VERCEL_GIT_COMMIT_SHA`). Elle voyage avec le bundle : c'est la version
//     que l'onglet exécute RÉELLEMENT.
//   - SERVEUR : `GET /api/version` → `{ build }` lu de `VERCEL_GIT_COMMIT_SHA`
//     au runtime. C'est la version en ligne MAINTENANT.
//   Différentes ⇒ l'onglet est périmé ⇒ bandeau « Rechargez la page ».
//
// ⚠️ LES DEUX EMPREINTES DOIVENT RESTER STRICTEMENT LE MÊME TEXTE. Toute
// troncature (7 caractères), hachage ou normalisation du sha doit se faire
// DES DEUX CÔTÉS — api/version.js ET src/lib/version-app.js — dans le même
// commit. Un serveur qui tronque face à un bundle qui ne tronque pas (ou
// l'inverse) rend les empreintes différentes à jamais : bandeau PERMANENT,
// que recharger ne fait pas disparaître. Aujourd'hui : aucun des deux ne
// transforme la valeur (trim uniquement, côté client).
//
// FAIL-OPEN TOTAL — LA RÈGLE QUI PRIME : ce contrôle ne doit JAMAIS afficher
// un bandeau à tort. Un faux « nouvelle version » ferait recharger pour rien,
// puis apprendrait à Dimitri à l'ignorer — et le jour où il compte, il
// ne serait plus lu. Donc : réseau coupé, 404, 500, JSON illisible, corps sans
// `build`, empreinte `local` d'un côté ou de l'autre (dev, CI, variable
// absente) ⇒ RIEN. Le seul cas qui déclenche : deux vraies empreintes,
// différentes.
//
// LE VERDICT SUIT LE SERVEUR : périmée, l'application continue d'interroger
// (au même rythme) — un déploiement SUIVANT change encore l'empreinte vue, et
// le bandeau, s'il avait été fermé, revient. Recharger remet tout à zéro par
// construction.

import { useEffect, useRef, useState } from "react";

// Valeur « je ne sais pas » — posée par le script build hors Vercel
// (`${VERCEL_GIT_COMMIT_SHA:-local}`) et par api/version.js hors Vercel.
export const EMPREINTE_INCONNUE = "local";

export const VERSION_APP_URL = "/api/version";

// Contrôle périodique : 10 min. Le retour de focus couvre le cas courant
// (Dimitri revient sur l'onglet) ; l'intervalle couvre l'onglet resté au
// premier plan pendant un déploiement.
export const VERSION_APP_INTERVALLE_MS = 10 * 60 * 1000;

// Anti-rafale : deux requêtes à moins de 30 s d'écart, c'est une de trop —
// des alt-tab répétés déclenchent `focus` à chaque retour. Le contrôle du
// montage n'est jamais retenu (compteur à zéro).
export const VERSION_APP_GARDE_MS = 30 * 1000;

// Le « × » n'est PAS un « ne plus me le dire » : un onglet rendu muet pour
// toute la session, c'est exactement l'état de l'incident du 22/09 — avec en
// plus le sentiment d'avoir été averti. La fermeture est donc horodatée et
// liée à l'empreinte serveur vue : le bandeau revient après 30 min, et tout de
// suite si un déploiement SUIVANT change encore l'empreinte.
export const VERSION_APP_CLE_FERMEE = "fbz-version-perimee-fermee";
export const VERSION_APP_REAFFICHAGE_MS = 30 * 60 * 1000;

// Une empreinte « vraie » = chaîne non vide, différente de la valeur inconnue.
// Tout le reste (undefined, null, nombre, objet, '', 'local') = inconnue.
export function empreinteConnue(valeur) {
  return (
    typeof valeur === "string" &&
    valeur.trim() !== "" &&
    valeur.trim() !== EMPREINTE_INCONNUE
  );
}

// L'empreinte compilée dans CE bundle. CRA n'inline que les `REACT_APP_*` ;
// absente (Jest, `npm start` sans la variable) ⇒ inconnue.
export function empreinteCompilee() {
  const sha = process.env.REACT_APP_BUILD_SHA;
  return empreinteConnue(sha) ? sha.trim() : EMPREINTE_INCONNUE;
}

// LE VERDICT, pur : périmée SEULEMENT si les deux empreintes sont connues
// ET différentes. C'est ici que vit le fail-open ; tout le reste n'est que
// plomberie autour.
export function estPerimee(compilee, serveur) {
  if (!empreinteConnue(compilee) || !empreinteConnue(serveur)) return false;
  return compilee.trim() !== serveur.trim();
}

// Lit l'empreinte du serveur. Rend une chaîne connue, ou `null` sur TOUTE
// panne (réseau, statut non-2xx, JSON illisible, corps sans `build`, `local`).
// Ne jette jamais.
export async function lireEmpreinteServeur(fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return null;
  try {
    const res = await doFetch(VERSION_APP_URL, {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res || !res.ok) return null;
    const corps = await res.json();
    const build = corps && corps.build;
    return empreinteConnue(build) ? build.trim() : null;
  } catch (e) {
    // Panne réseau ou JSON illisible : silence voulu (fail-open). Pas de
    // console.error — ce contrôle tourne toutes les 10 min et au focus, un
    // réseau instable inonderait la console pour un contrôle facultatif.
    return null;
  }
}

// Mémoire de la fermeture du bandeau : { ts, serveur } en sessionStorage.
// sessionStorage peut être absent ou refuser l'accès (navigation privée
// stricte) : dans le doute, « pas fermé » à la lecture, et une écriture qui
// échoue est ignorée.
export function lireFermeture() {
  try {
    const brut = window.sessionStorage.getItem(VERSION_APP_CLE_FERMEE);
    if (!brut) return null;
    const rec = JSON.parse(brut);
    if (!rec || typeof rec.ts !== "number" || !empreinteConnue(rec.serveur))
      return null;
    return { ts: rec.ts, serveur: rec.serveur };
  } catch (e) {
    return null;
  }
}

export function fermerLeBandeau(serveur, maintenant = Date.now()) {
  try {
    window.sessionStorage.setItem(
      VERSION_APP_CLE_FERMEE,
      JSON.stringify({ ts: maintenant, serveur }),
    );
  } catch (e) {
    // Sans sessionStorage, la fermeture ne vaut que pour ce rendu : acceptable.
  }
}

// Fermé « encore » = fermé pour CETTE empreinte serveur, il y a moins de
// `reaffichageMs`. Une autre empreinte (déploiement suivant) ou un délai
// écoulé ⇒ le bandeau revient. Rend aussi l'instant du retour (pour poser un
// minuteur), `null` si pas fermé.
export function fermetureEnCours(
  serveur,
  maintenant = Date.now(),
  reaffichageMs = VERSION_APP_REAFFICHAGE_MS,
) {
  const rec = lireFermeture();
  if (!rec || rec.serveur !== serveur) return null;
  const retourA = rec.ts + reaffichageMs;
  if (maintenant >= retourA) return null;
  return { retourA };
}

// LE HOOK. Interroge le serveur au montage, à chaque retour de focus /
// visibilité, et toutes les `intervalleMs` — jamais onglet caché, jamais deux
// fois en moins de `gardeMs`. `serveur` = dernière empreinte
// serveur connue (une panne ultérieure ne l'efface pas) ; `perimee` en est
// dérivée, et suit donc les déploiements suivants.
//
// Options (toutes facultatives, pour les tests) :
//   compilee    — remplace `empreinteCompilee()` (Jest n'a pas la variable).
//   fetchImpl   — remplace `fetch` global.
//   intervalleMs — remplace les 10 min.
//   gardeMs     — remplace les 30 s d'anti-rafale.
export function useVersionApp(options = {}) {
  const {
    fetchImpl,
    intervalleMs = VERSION_APP_INTERVALLE_MS,
    gardeMs = VERSION_APP_GARDE_MS,
  } = options;
  const compilee =
    options.compilee === undefined ? empreinteCompilee() : options.compilee;
  const [serveur, setServeur] = useState(null);
  // Une requête à la fois : focus + visibilitychange arrivent souvent ensemble.
  const enCoursRef = useRef(false);
  // Instant de la dernière requête (anti-rafale).
  const dernierRef = useRef(0);

  useEffect(() => {
    // Bundle sans empreinte (dev, Jest) ⇒ on ne demande même pas au serveur :
    // la réponse ne pourrait rien conclure.
    if (!empreinteConnue(compilee)) return undefined;
    let actif = true;

    const verifier = async () => {
      if (!actif || enCoursRef.current) return;
      // Onglet caché : personne ne lira le verdict — ni au retour d'un
      // événement, ni à l'intervalle (un onglet caché toute la nuit tirait
      // 144 requêtes pour rien).
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      )
        return;
      if (Date.now() - dernierRef.current < gardeMs) return;
      dernierRef.current = Date.now();
      enCoursRef.current = true;
      try {
        const vu = await lireEmpreinteServeur(fetchImpl);
        if (actif && vu) setServeur((avant) => (avant === vu ? avant : vu));
      } finally {
        enCoursRef.current = false;
      }
    };

    verifier();
    window.addEventListener("focus", verifier);
    document.addEventListener("visibilitychange", verifier);
    const timer = setInterval(verifier, intervalleMs);
    return () => {
      actif = false;
      // StrictMode (dev) monte, démonte, remonte : sans cette remise à zéro, le
      // second montage voit une requête « en cours » dont le résultat est déjà
      // jeté (actif=false) — 1 fetch, 0 verdict, en silence. Prod non affectée,
      // mais la fonctionnalité devenait invérifiable en dev.
      enCoursRef.current = false;
      dernierRef.current = 0;
      window.removeEventListener("focus", verifier);
      document.removeEventListener("visibilitychange", verifier);
      clearInterval(timer);
    };
  }, [compilee, fetchImpl, intervalleMs, gardeMs]);

  return { perimee: estPerimee(compilee, serveur), compilee, serveur };
}
